<template>
  <component :is="linkComponent"
    v-bind="linkAttrs"
    :class="['card-link', { 'card-link--clickable': isValidLink }]"
    :aria-label="isValidLink ? (isExternalLink ? `Read article: ${heading} (opens in a new window)` : `Read article: ${heading}`) : undefined">
    <div class="card">
      <!-- Header -->
      <div class="card__header">
        <img :src="image" alt="" aria-hidden="true" class="img-fluid card__image" />
      </div>

      <!-- Content -->
      <div class="card__content">
        <!-- Date with calendar icon -->
        <!-- <div class="card__heading">
          <span class="calendar-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="16"
              width="16"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path
                d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 
                   1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 
                   16H5V9h14v11zm0-13H5V6h14v1z"
              />
            </svg>
          </span>
          <span :style="{ color: colors.primaryText }">{{ createdAt }}</span>
        </div> -->

        <!-- Title -->
        <div class="card__info">
          <span class="card__title" :style="{ color: colors.primaryText }">
            {{ heading.slice(0, 111) }}
          </span>
        </div>
      </div>
    </div>
  </component>
</template>

<script setup>
import { useTheme } from "~/composables/useTheme";
import { computed, resolveComponent } from "vue";

const { colors } = useTheme();

const props = defineProps({
  image: { type: String, required: true },
  createdAt: { type: String, default: "" },
  heading: { type: String, required: true },
  src: { type: String, required: true }, // new prop for link
});

// Render links for both external media URLs and internal press-release routes.
const isValidLink = computed(() => {
  const s = props.src?.trim();
  return s && (s.startsWith("http://") || s.startsWith("https://") || s.startsWith("/"));
});

const isExternalLink = computed(() => {
  const s = props.src?.trim();
  return s && (s.startsWith("http://") || s.startsWith("https://"));
});

const linkComponent = computed(() => {
  if (!isValidLink.value) return "div";
  return isExternalLink.value ? "a" : resolveComponent("NuxtLink");
});

const linkAttrs = computed(() => {
  if (!isValidLink.value) return {};
  if (isExternalLink.value) {
    return { href: props.src, target: "_blank", rel: "noopener noreferrer" };
  }
  return { to: props.src };
});
</script>

<style scoped>
.card-link {
  text-decoration: none;
  color: inherit;
  width: 96%;
  display: block;
}

.card-link--clickable {
  cursor: pointer;
}

.card-link:focus-visible {
  outline: 2px solid var(--primaryColor);
  border-radius: 14px;
}

.card {
  border: 1px solid var(--secondaryText);
  border-radius: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  height: 100%;
  min-height: 290px;
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}

.card__header {
  display: flex;
  justify-content: center;
}

.card__image {
  width: 100%;
  max-height: 200px;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  object-fit: cover;
}

.card__content {
  padding: 16px;
}

.card__heading {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  margin-bottom: 8px;
  font-weight: 600;
}

.calendar-icon {
  display: flex;
  align-items: center;
  color: #555;
}

.card__info {
  font-weight: 600;
  font-size: 16px;
}
</style>
